import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Copy, Calendar, MapPin, LogOut, Plus, CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

/*  tweddi — Host Dashboard (/admin)
    tweddi identity: cream ground, Inclusive Sans, near-mono ink with apricot
    only as a rare tiny accent, the woven-rings monogram (halo bg always matches
    what's behind it), tiny wide-tracked labels, hairline rules, circle-arrow
    buttons. Logic 100% preserved. Pre-payment pitch aligned with the elevated
    Marketing direction (tighter promise, guest-pays-nothing, no subscriptions). */

const API   = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CREAM = '#FFFDF8';
const WASH  = '#FBF6EF';
const INK   = '#141313';
const MUTE  = '#6E6B62';
const APRICOT = '#F4A26B';
const RULE  = 'rgba(20,19,19,0.10)';
const HAIR  = 'rgba(20,19,19,0.06)';

/* woven monogram — pass the true background behind it as `bg` */
const Mark = ({ size = 26, mark = INK, bg = CREAM, style }) => (
  <svg viewBox="-60 -60 664 520" width={size} height={size * (520/664)} style={{ overflow: 'visible', display: 'block', flexShrink: 0, ...style }} aria-label="tweddi">
    <circle cx="200" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={bg}   strokeWidth="108" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={bg}   strokeWidth="108" />
    <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={mark} strokeWidth="66" />
  </svg>
);
const Wordmark = ({ mark = INK, bg = CREAM, onClick }) => (
  <div onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
    <Mark size={24} mark={mark} bg={bg} />
    <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.1rem', fontWeight: 600, letterSpacing: '-0.01em', color: mark }}>tweddi</span>
  </div>
);

const Label = ({ children, mb = '0.75rem' }) => (
  <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: INK, marginBottom: mb, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>{children}</p>
);
const CircleArrow = ({ size = 30 }) => (
  <span className="tw-circle" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: '50%', background: INK, flexShrink: 0, transition: 'transform 0.25s cubic-bezier(.16,1,.3,1)' }}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={CREAM} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
  </span>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, updateUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState({ guests: 0, matches: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [eventPrice, setEventPrice] = useState(99.00);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [liveStats, setLiveStats] = useState(null);
  const [isWeddingDay, setIsWeddingDay] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [eventForm, setEventForm] = useState({ bride_name: '', groom_name: '', wedding_date: '', venue: '' });

  useEffect(() => {
    fetchEventData(); checkPaymentStatus(); fetchLiveStats(); checkWeddingDay();
    const paymentId = searchParams.get('payment_id');
    if (paymentId) pollPaymentStatus(paymentId);
  }, [searchParams]);

  const fetchLiveStats = async () => { try { const res = await axios.get(`${API}/events/live-stats`); setLiveStats(res.data); } catch {} };
  const checkWeddingDay = async () => { try { const res = await axios.get(`${API}/events/wedding-day-mode`); setIsWeddingDay(res.data.is_wedding_day); } catch {} };
  const checkPaymentStatus = async () => { try { const res = await axios.get(`${API}/payments/check`); setHasPaid(res.data.has_paid); setEventPrice(res.data.price); } catch {} };
  const pollPaymentStatus = async (paymentId, attempts = 0) => {
    if (attempts >= 5) { toast.error('Payment verification timed out.'); setIsProcessingPayment(false); return; }
    setIsProcessingPayment(true);
    try {
      const res = await axios.get(`${API}/payments/status/${paymentId}`);
      if (res.data.payment_status === 'paid') { setHasPaid(true); setPaymentSuccess(true); setIsProcessingPayment(false); toast.success('Payment confirmed.'); window.history.replaceState({}, document.title, window.location.pathname); return; }
      else if (['expired','failed','canceled'].includes(res.data.status)) { toast.error('Payment was not completed.'); setIsProcessingPayment(false); window.history.replaceState({}, document.title, window.location.pathname); return; }
      setTimeout(() => pollPaymentStatus(paymentId, attempts + 1), 2000);
    } catch { setTimeout(() => pollPaymentStatus(paymentId, attempts + 1), 2000); }
  };
  const fetchEventData = async () => {
    try {
      const eventRes = await axios.get(`${API}/events/current`);
      if (eventRes.data.event) { setEvent(eventRes.data.event); const statsRes = await axios.get(`${API}/events/${eventRes.data.event.id}/stats`); setStats({ guests: statsRes.data.guests, matches: statsRes.data.matches }); }
    } catch {} finally { setIsLoading(false); }
  };
  const validatePromoCode = async () => {
    if (!promoCode.trim()) return; setPromoLoading(true);
    try { const res = await axios.post(`${API}/promo/validate`, { code: promoCode }); setPromoResult(res.data); toast.success(`${res.data.description} applied!`); }
    catch { setPromoResult(null); toast.error('Invalid promo code'); } finally { setPromoLoading(false); }
  };
  const handlePayment = async () => {
    setIsProcessingPayment(true);
    try {
      const codeToSend = (promoResult?.code || promoCode || '').trim().toUpperCase() || null;
      const res = await axios.post(`${API}/payments/checkout`, { origin_url: window.location.origin, promo_code: codeToSend });
      if (res.data.paid_free || res.data.checkout_url == null) { setHasPaid(true); setPaymentSuccess(true); setIsProcessingPayment(false); toast.success('Your wedding is unlocked.'); await checkPaymentStatus(); return; }
      const w = window.open(res.data.checkout_url, '_blank');
      if (!w) window.location.href = res.data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Payment failed'); setIsProcessingPayment(false); }
  };
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.bride_name || !eventForm.groom_name || !eventForm.wedding_date) { toast.error('Please fill in all required fields'); return; }
    setIsCreating(true);
    try { const res = await axios.post(`${API}/events/create`, eventForm); setEvent(res.data.event); updateUser({ event_id: res.data.event.id }); setShowCreateDialog(false); toast.success('Event created.'); }
    catch (err) { if (err.response?.status === 402) { toast.error('Please complete payment first'); setHasPaid(false); } else toast.error(err.response?.data?.detail || 'Failed to create event'); }
    finally { setIsCreating(false); }
  };
  const copyEventCode = () => { if (event?.code) { navigator.clipboard.writeText(event.code); toast.success('Code copied'); } };
  const handleLogout = () => { logout(); navigate('/'); };

  const inputStyle = { width: '100%', border: 'none', borderBottom: `1.5px solid ${RULE}`, background: 'transparent', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.95rem', fontWeight: 600, color: INK, padding: '0.6rem 0', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' };

  if (isLoading) return (
    <div style={{ minHeight: '100dvh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '1.25rem', height: '1.25rem', border: `1.5px solid ${RULE}`, borderTopColor: INK, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ background: `linear-gradient(180deg, ${CREAM} 55%, ${WASH} 100%)`, minHeight: '100dvh', maxWidth: '32rem', margin: '0 auto', position: 'relative' }} data-testid="admin-dashboard">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        * { box-sizing: border-box; }
        .ad-h1 { font-family: 'Inclusive Sans', sans-serif; font-weight: 600; letter-spacing: -0.03em; line-height: 1.04; color: ${INK}; margin: 0; }
        .ad-num { font-family: 'Inclusive Sans', sans-serif; font-weight: 700; letter-spacing: -0.03em; line-height: 1; color: ${INK}; }
        .tw-circle:hover { transform: translateX(3px); }
        .bb-input:focus { border-bottom-color: ${INK} !important; }
        .bb-input::placeholder { color: rgba(20,19,19,0.35); font-weight: 500; }
        .ad-link { position: relative; transition: color .2s; }
        .ad-link:hover { color: ${INK} !important; }
        .ad-twinkle { display: inline-block; animation: ad-tw 2.6s ease-in-out infinite; transform-origin: center; }
        @keyframes ad-tw { 0%,100%{transform:scale(1) rotate(0);opacity:.7} 50%{transform:scale(1.3) rotate(90deg);opacity:1} }
        .ad-beat { display: inline-block; animation: ad-bt 1.8s ease-in-out infinite; }
        @keyframes ad-bt { 0%,100%{transform:scale(1)} 15%{transform:scale(1.3)} 30%{transform:scale(1)} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
        .ad-rise { opacity: 0; transform: translateY(16px); animation: ad-rise .8s cubic-bezier(.16,1,.3,1) forwards; }
        @keyframes ad-rise { to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .ad-rise,.ad-twinkle,.ad-beat { animation: none; opacity: 1; transform: none; } }
      `}</style>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(255,253,248,0.9)', backdropFilter: 'blur(16px)', borderBottom: `1px solid ${HAIR}`, padding: '1.05rem 1.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Wordmark onClick={() => navigate('/')} />
        <button onClick={handleLogout} data-testid="logout-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTE, display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'color 0.2s' }} className="ad-link">
          <LogOut size={13} strokeWidth={1.75} /> Sign out
        </button>
      </header>

      <main style={{ padding: '2.75rem 1.75rem 4rem' }}>
        {/* Title */}
        <div className="ad-rise" style={{ borderTop: `1px solid ${INK}`, paddingTop: '1.5rem', marginBottom: '2.75rem', animationDelay: '.05s' }}>
          <Label>Host dashboard <span className="ad-twinkle" style={{ fontSize: '0.7rem', color: APRICOT }}>✳</span></Label>
          <h1 className="ad-h1" style={{ fontSize: 'clamp(2.5rem, 9vw, 3.4rem)' }}>
            {event ? <>{event.bride_name} &amp; {event.groom_name}</> : <>Your wedding,<br />one code.</>}
          </h1>
        </div>

        {isProcessingPayment && (
          <div style={{ padding: '3rem 0', textAlign: 'center' }}>
            <div className="spin" style={{ width: '1.25rem', height: '1.25rem', border: `1.5px solid ${RULE}`, borderTopColor: INK, borderRadius: '50%', margin: '0 auto 1.25rem' }} />
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.05rem', fontStyle: 'italic', color: INK }}>Verifying payment…</p>
          </div>
        )}
        {paymentSuccess && !event && (
          <div style={{ borderLeft: `2px solid ${INK}`, paddingLeft: '1rem', marginBottom: '2rem' }}>
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.05rem', fontStyle: 'italic', color: INK, marginBottom: '0.25rem' }}>Payment confirmed.</p>
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: MUTE }}>You can create your wedding below.</p>
          </div>
        )}

        {event ? (
          <>
            {/* Event meta */}
            <div className="ad-rise" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '2.25rem', animationDelay: '.12s' }}>
              {event.wedding_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 500, color: MUTE }}>
                  <Calendar size={13} strokeWidth={1.75} />{format(new Date(event.wedding_date), 'MMMM d, yyyy')}
                </div>
              )}
              {event.venue && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 500, color: MUTE }}>
                  <MapPin size={13} strokeWidth={1.75} />{event.venue}
                </div>
              )}
            </div>

            {/* Code block — dark ink card */}
            <div className="ad-rise" style={{ background: INK, borderRadius: '1.25rem', padding: '1.5rem 1.75rem', marginBottom: '2.25rem', animationDelay: '.18s' }}>
              <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,253,248,0.55)', marginBottom: '0.75rem' }}>Wedding code</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: 'clamp(2rem, 8vw, 2.75rem)', fontWeight: 700, color: CREAM, letterSpacing: '0.18em', lineHeight: 1 }}>{event.code}</span>
                <button onClick={copyEventCode} data-testid="copy-code-btn" style={{ background: CREAM, color: INK, border: 'none', borderRadius: '9999px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.6rem 1.1rem' }}>
                  <Copy size={12} strokeWidth={2} /> Copy
                </button>
              </div>
              <div style={{ marginTop: '1.1rem', paddingTop: '1.1rem', borderTop: '1px solid rgba(255,253,248,0.12)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/countdown/${event.code}`); toast.success('Countdown link copied!'); }} data-testid="share-countdown-btn" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.66rem', fontWeight: 600, color: 'rgba(255,253,248,0.75)', letterSpacing: '0.02em' }} className="ad-link">
                  Share countdown →
                </button>
                <a href={`${API}/countdown/${event.code}/qr`} download={`${event.code}-qr.png`} data-testid="download-qr-btn" style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.66rem', fontWeight: 600, color: 'rgba(255,253,248,0.75)', textDecoration: 'none', letterSpacing: '0.02em' }} className="ad-link">
                  Download QR →
                </a>
              </div>
            </div>

            {/* Stats */}
            <div className="ad-rise" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: `1px solid ${RULE}`, marginBottom: '2.25rem', animationDelay: '.24s' }}>
              {[{ num: stats.guests, label: 'Single guests' }, { num: stats.matches, label: 'Matches made' }].map((s, i) => (
                <div key={i} style={{ padding: '1.4rem 0', paddingRight: i === 0 ? '1.5rem' : 0, paddingLeft: i === 1 ? '1.5rem' : 0, borderLeft: i === 1 ? `1px solid ${RULE}` : 'none' }}>
                  <p className="ad-num" style={{ fontSize: '3rem', marginBottom: '0.4rem' }}>{s.num}</p>
                  <Label mb="0">{s.label}</Label>
                </div>
              ))}
            </div>

            {/* Wedding day */}
            {isWeddingDay && (
              <div style={{ borderLeft: `2px solid ${INK}`, paddingLeft: '1rem', marginBottom: '2.25rem' }} data-testid="admin-wedding-day-banner">
                <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.15rem', fontStyle: 'italic', color: INK, marginBottom: '0.15rem' }}>It's the big day.</p>
                <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: MUTE }}>Wedding Day Mode is live for your guests.</p>
              </div>
            )}

            {/* Live activity */}
            {liveStats && (
              <div className="ad-rise" style={{ borderTop: `1px solid ${RULE}`, paddingTop: '1.4rem', marginBottom: '2.25rem' }} data-testid="admin-live-stats">
                <Label>Live activity</Label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', marginBottom: '1.25rem' }}>
                  {[{ num: liveStats.total_guests, label: 'Guests' }, { num: liveStats.total_matches, label: 'Matches' }, { num: liveStats.today_matches, label: 'Today' }].map((s, i) => (
                    <div key={i} style={{ paddingTop: '0.5rem', borderLeft: i > 0 ? `1px solid ${RULE}` : 'none', paddingLeft: i > 0 ? '1rem' : 0, paddingRight: i < 2 ? '1rem' : 0 }}>
                      <p className="ad-num" style={{ fontSize: '2rem', marginBottom: '0.35rem' }}>{s.num}</p>
                      <Label mb="0">{s.label}</Label>
                    </div>
                  ))}
                </div>
                {liveStats.recent_match_names?.length > 0 && (
                  <>
                    <Label>Recent matches <span className="ad-beat" style={{ fontSize: '0.62rem', color: APRICOT }}>♥</span></Label>
                    {liveStats.recent_match_names.map((name, i) => (
                      <div key={i} style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.8rem', fontWeight: 500, color: MUTE, padding: '0.5rem 0', borderBottom: `1px solid ${HAIR}` }}>{name}</div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Tips */}
            <div style={{ borderTop: `1px solid ${RULE}`, paddingTop: '1.4rem' }}>
              <Label>Tips</Label>
              {['Print the QR code on your invitations so guests can join early', 'Share the countdown link to build anticipation', 'Announce the Bouquet Toss during the reception', 'Guests can keep chatting until 30 days after the wedding'].map((tip, i) => (
                <div key={i} style={{ display: 'flex', gap: '1.1rem', padding: '0.85rem 0', borderBottom: `1px solid ${HAIR}` }}>
                  <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.62rem', fontWeight: 700, color: MUTE, letterSpacing: '0.04em', flexShrink: 0, minWidth: '1.5rem', paddingTop: '0.15rem' }}>0{i + 1}</span>
                  <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.6 }}>{tip}</span>
                </div>
              ))}
            </div>
          </>
        ) : !isProcessingPayment && (
          <>
            {!hasPaid ? (
              <>
                <p className="ad-rise" style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, color: INK, lineHeight: 1.6, marginBottom: '2.25rem', maxWidth: '23rem', animationDelay: '.12s' }}>
                  One code turns your guest list into introductions. tweddi privately connects the single people at your wedding — before, during, and after the day.
                </p>
                {/* Price */}
                <div className="ad-rise" style={{ borderTop: `1px solid ${RULE}`, paddingTop: '1.5rem', marginBottom: '1.75rem', animationDelay: '.18s' }}>
                  <Label>One flat fee</Label>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
                    <p className="ad-num" style={{ fontSize: 'clamp(3.5rem, 14vw, 4.5rem)' }}>€{eventPrice}</p>
                    <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: MUTE }}>one-time</span>
                  </div>
                  <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.68rem', fontWeight: 500, color: MUTE, letterSpacing: '0.02em', marginTop: '0.4rem' }}>No subscriptions, no per-guest fees. Your guests join free.</p>
                </div>
                {/* Features */}
                <div className="ad-rise" style={{ animationDelay: '.24s' }}>
                  {['A code minted for your wedding', 'Unlimited guest opt-ins', 'Live from the day you share it', 'Stays open 30 days after the wedding', 'Live match analytics'].map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: `1px solid ${HAIR}` }}>
                      <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: INK }}>{f}</span>
                      <span style={{ fontSize: '0.7rem', color: APRICOT }}>✓</span>
                    </div>
                  ))}
                </div>
                {/* Promo */}
                <div style={{ borderTop: `1px solid ${RULE}`, paddingTop: '1.4rem', marginTop: '1.75rem', marginBottom: '2rem' }}>
                  <Label>Promo code</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <input className="bb-input" value={promoCode} onChange={(e) => { setPromoCode(e.target.value); setPromoResult(null); }} placeholder="Enter code" data-testid="promo-input" style={{ flex: 1, border: 'none', borderBottom: `1.5px solid ${RULE}`, background: 'transparent', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.9rem', fontWeight: 600, color: INK, padding: '0.6rem 0', outline: 'none', letterSpacing: '0.08em', textTransform: 'uppercase' }} />
                    <button onClick={validatePromoCode} disabled={!promoCode.trim() || promoLoading} data-testid="apply-promo-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: promoCode.trim() ? INK : MUTE, opacity: promoLoading ? 0.5 : 1, transition: 'color 0.2s', padding: 0 }}>
                      {promoLoading ? '…' : 'Apply'}
                    </button>
                  </div>
                  {promoResult && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.85rem', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 600, color: INK }}>
                      <span>{promoResult.description}</span>
                      <span>{promoResult.final_price > 0 ? `€${promoResult.final_price}` : 'Free'}</span>
                    </div>
                  )}
                </div>
                {/* Pay */}
                <button onClick={handlePayment} disabled={isProcessingPayment} data-testid="pay-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, opacity: isProcessingPayment ? 0.5 : 1 }}>
                  {promoResult ? promoResult.final_price > 0 ? `Pay €${promoResult.final_price}` : 'Claim free event' : `Pay €${eventPrice}`}
                  <CircleArrow size={32} />
                </button>
                {isProcessingPayment && (
                  <div style={{ marginTop: '1.4rem' }}>
                    <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 500, color: MUTE, lineHeight: 1.6, marginBottom: '0.6rem' }}>A payment window has opened. Complete it there, then return here.</p>
                    <button onClick={() => { setIsProcessingPayment(false); checkPaymentStatus(); }} data-testid="verify-payment-btn" style={{ background: 'none', border: 'none', borderBottom: `1.5px solid ${INK}`, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.66rem', fontWeight: 600, color: INK, padding: '0 0 2px', letterSpacing: '0.04em' }}>
                      I've completed payment
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.05rem', fontStyle: 'italic', color: INK, marginBottom: '2rem', borderLeft: `2px solid ${INK}`, paddingLeft: '1rem' }}>Payment confirmed. Create your wedding below.</p>
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <DialogTrigger asChild>
                    <button data-testid="create-event-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK }}>
                      Create your wedding <CircleArrow size={32} />
                    </button>
                  </DialogTrigger>
                  <DialogContent style={{ background: CREAM, border: `1px solid ${RULE}`, borderRadius: '1rem', maxWidth: '23rem', padding: '2rem' }}>
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.7rem', fontWeight: 600, color: INK, letterSpacing: '-0.03em' }}>Wedding details</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateEvent} style={{ paddingTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                        {[{ label: "Bride's name", key: 'bride_name', ph: 'Sarah', testid: 'bride-name-input' }, { label: "Groom's name", key: 'groom_name', ph: 'Michael', testid: 'groom-name-input' }].map(f => (
                          <div key={f.key}>
                            <Label mb="0.4rem">{f.label}</Label>
                            <input className="bb-input" value={eventForm[f.key]} onChange={(e) => setEventForm({ ...eventForm, [f.key]: e.target.value })} placeholder={f.ph} required style={inputStyle} data-testid={f.testid} />
                          </div>
                        ))}
                      </div>
                      <div>
                        <Label mb="0.4rem">Wedding date</Label>
                        <input className="bb-input" type="date" value={eventForm.wedding_date} onChange={(e) => setEventForm({ ...eventForm, wedding_date: e.target.value })} required style={inputStyle} data-testid="wedding-date-input" />
                      </div>
                      <div>
                        <Label mb="0.4rem">Venue <span style={{ color: MUTE, fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}>(optional)</span></Label>
                        <input className="bb-input" value={eventForm.venue} onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })} placeholder="The Grand Ballroom" style={inputStyle} data-testid="venue-input" />
                      </div>
                      <button type="submit" disabled={isCreating} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, opacity: isCreating ? 0.5 : 1, marginTop: '0.5rem' }} data-testid="submit-event-btn">
                        {isCreating ? 'Creating…' : 'Create wedding'} <CircleArrow size={32} />
                      </button>
                    </form>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
